// Typed API endpoint methods for the consumer SPA. Returns ApiResult<T>
// from client.ts.

import type {
  AcceptCoachingContractRequest,
  AcceptCoachingContractResponse,
  ActivateFrameworkRequest,
  ActivateFrameworkResponse,
  ActivityId,
  AuthMeResponse,
  CreateCoachProjectRequest,
  CreateCoachProjectResponse,
  CreateCoachSessionRequest,
  CreateCoachSessionResponse,
  DeleteCoachProjectResponse,
  DeleteCoachSessionRequest,
  DeleteCoachSessionResponse,
  GetCoachProjectResponse,
  ListCoachProjectsResponse,
  PatchCoachProjectRequest,
  PatchCoachProjectResponse,
  DiscontinueInstanceResponse,
  FrameworkId,
  FrameworkInstanceId,
  GetCoachingContractResponse,
  GetCoachSessionResponse,
  GetMeFrameworkDetailResponse,
  GetMeFrameworkInstanceResponse,
  GetMeFrameworksResponse,
  HealthResponse,
  ListCoachMessagesResponse,
  ListCoachSessionsResponse,
  ListMeFrameworkInstancesResponse,
  ListSessionMessagesResponse,
  PatchCoachSessionRequest,
  PatchCoachSessionResponse,
  SearchCoachSessionsResponse,
  PatchInstanceActivityRequest,
  PatchInstanceActivityResponse,
  PatchProfileRequest,
  PatchProfileResponse,
  SendCoachFeedbackRequest,
  SendCoachFeedbackResponse,
  SendCoachMessageRequest,
  SendCoachMessageResponse,
} from '@gc-coach/shared';
import { apiFetch, type ApiResult } from './client.js';

export function getHealth(): Promise<ApiResult<HealthResponse>> {
  return apiFetch<HealthResponse>('/health', { auth: false });
}

export function getMe(): Promise<ApiResult<AuthMeResponse>> {
  return apiFetch<AuthMeResponse>('/auth/me');
}

// ─── /me API (consumer-pool only — admin sessions get 401) ────────────────

export function getCoachingContract(): Promise<ApiResult<GetCoachingContractResponse>> {
  return apiFetch<GetCoachingContractResponse>('/me/coaching-contract');
}

export function acceptCoachingContract(
  body: AcceptCoachingContractRequest,
): Promise<ApiResult<AcceptCoachingContractResponse>> {
  return apiFetch<AcceptCoachingContractResponse>('/me/coaching-contract/accept', {
    method: 'POST',
    body,
  });
}

export function patchProfile(
  body: PatchProfileRequest,
): Promise<ApiResult<PatchProfileResponse>> {
  return apiFetch<PatchProfileResponse>('/me/profile', {
    method: 'PATCH',
    body,
  });
}

// ─── Coach surface ────────────────────────────────────────────────────────

/** @deprecated Use listSessionMessages() with a specific sessionId.
 *  Kept until MR C ships the sessions rail and the old flat-thread Coach UI
 *  is removed. */
export function listCoachMessages(): Promise<ApiResult<ListCoachMessagesResponse>> {
  return apiFetch<ListCoachMessagesResponse>('/me/coach/messages');
}

export function listCoachSessions(options?: {
  frameworkInstanceId?: string;
  includeArchived?: boolean;
}): Promise<ApiResult<ListCoachSessionsResponse>> {
  const query = new URLSearchParams();
  if (options?.frameworkInstanceId) {
    query.set('framework_instance_id', options.frameworkInstanceId);
  }
  if (options?.includeArchived) query.set('include_archived', 'true');
  const qs = query.toString();
  return apiFetch<ListCoachSessionsResponse>(`/me/coach/sessions${qs ? `?${qs}` : ''}`);
}

export function searchCoachSessions(
  query: string,
  options?: { includeArchived?: boolean; limit?: number },
): Promise<ApiResult<SearchCoachSessionsResponse>> {
  const qs = new URLSearchParams({ q: query });
  if (options?.includeArchived) qs.set('include_archived', 'true');
  if (options?.limit) qs.set('limit', String(options.limit));
  return apiFetch<SearchCoachSessionsResponse>(
    `/me/coach/sessions/search?${qs.toString()}`,
  );
}

export function createCoachSession(
  body: CreateCoachSessionRequest = {},
): Promise<ApiResult<CreateCoachSessionResponse>> {
  return apiFetch<CreateCoachSessionResponse>('/me/coach/sessions', {
    method: 'POST',
    body,
  });
}

export function getCoachSession(
  sessionId: string,
): Promise<ApiResult<GetCoachSessionResponse>> {
  return apiFetch<GetCoachSessionResponse>(
    `/me/coach/sessions/${encodeURIComponent(sessionId)}`,
  );
}

export function patchCoachSession(
  sessionId: string,
  body: PatchCoachSessionRequest,
): Promise<ApiResult<PatchCoachSessionResponse>> {
  return apiFetch<PatchCoachSessionResponse>(
    `/me/coach/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'PATCH', body },
  );
}

export function deleteCoachSession(
  sessionId: string,
  body: DeleteCoachSessionRequest = {},
): Promise<ApiResult<DeleteCoachSessionResponse>> {
  return apiFetch<DeleteCoachSessionResponse>(
    `/me/coach/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE', body },
  );
}

export function listSessionMessages(
  sessionId: string,
): Promise<ApiResult<ListSessionMessagesResponse>> {
  return apiFetch<ListSessionMessagesResponse>(
    `/me/coach/sessions/${encodeURIComponent(sessionId)}/messages`,
  );
}

export function sendCoachMessage(
  body: SendCoachMessageRequest,
): Promise<ApiResult<SendCoachMessageResponse>> {
  return apiFetch<SendCoachMessageResponse>('/me/coach/messages', {
    method: 'POST',
    body,
  });
}

export function sendCoachFeedback(
  body: SendCoachFeedbackRequest,
): Promise<ApiResult<SendCoachFeedbackResponse>> {
  return apiFetch<SendCoachFeedbackResponse>('/me/coach/feedback', {
    method: 'POST',
    body,
  });
}

// ─── Frameworks surface (consumer-pool only) ──────────────────────────────

export function getMeFrameworks(): Promise<ApiResult<GetMeFrameworksResponse>> {
  return apiFetch<GetMeFrameworksResponse>('/me/frameworks');
}

export function getMeFrameworkDetail(
  frameworkId: FrameworkId,
): Promise<ApiResult<GetMeFrameworkDetailResponse>> {
  return apiFetch<GetMeFrameworkDetailResponse>(
    `/me/frameworks/${encodeURIComponent(frameworkId)}`,
  );
}

export function activateFramework(
  frameworkId: FrameworkId,
  body: ActivateFrameworkRequest,
): Promise<ApiResult<ActivateFrameworkResponse>> {
  return apiFetch<ActivateFrameworkResponse>(
    `/me/frameworks/${encodeURIComponent(frameworkId)}/activate`,
    { method: 'POST', body },
  );
}

export function listMeFrameworkInstances(): Promise<ApiResult<ListMeFrameworkInstancesResponse>> {
  return apiFetch<ListMeFrameworkInstancesResponse>('/me/framework-instances');
}

export function getMeFrameworkInstance(
  instanceId: FrameworkInstanceId,
): Promise<ApiResult<GetMeFrameworkInstanceResponse>> {
  return apiFetch<GetMeFrameworkInstanceResponse>(
    `/me/framework-instances/${encodeURIComponent(instanceId)}`,
  );
}

export function patchInstanceActivity(
  instanceId: FrameworkInstanceId,
  activityId: ActivityId,
  body: PatchInstanceActivityRequest,
): Promise<ApiResult<PatchInstanceActivityResponse>> {
  return apiFetch<PatchInstanceActivityResponse>(
    `/me/framework-instances/${encodeURIComponent(instanceId)}/activities/${encodeURIComponent(activityId)}`,
    { method: 'PATCH', body },
  );
}

export function discontinueInstance(
  instanceId: FrameworkInstanceId,
): Promise<ApiResult<DiscontinueInstanceResponse>> {
  return apiFetch<DiscontinueInstanceResponse>(
    `/me/framework-instances/${encodeURIComponent(instanceId)}`,
    { method: 'DELETE' },
  );
}

// ─── Projects CRUD ───────────────────────────────────────────────────────

export function listCoachProjects(options?: {
  includeArchived?: boolean;
}): Promise<ApiResult<ListCoachProjectsResponse>> {
  const qs = options?.includeArchived ? '?include_archived=true' : '';
  return apiFetch<ListCoachProjectsResponse>(`/me/coach/projects${qs}`);
}

export function createCoachProject(
  body: CreateCoachProjectRequest,
): Promise<ApiResult<CreateCoachProjectResponse>> {
  return apiFetch<CreateCoachProjectResponse>('/me/coach/projects', {
    method: 'POST',
    body,
  });
}

export function getCoachProject(
  projectId: string,
): Promise<ApiResult<GetCoachProjectResponse>> {
  return apiFetch<GetCoachProjectResponse>(
    `/me/coach/projects/${encodeURIComponent(projectId)}`,
  );
}

export function patchCoachProject(
  projectId: string,
  body: PatchCoachProjectRequest,
): Promise<ApiResult<PatchCoachProjectResponse>> {
  return apiFetch<PatchCoachProjectResponse>(
    `/me/coach/projects/${encodeURIComponent(projectId)}`,
    { method: 'PATCH', body },
  );
}

export function deleteCoachProject(
  projectId: string,
): Promise<ApiResult<DeleteCoachProjectResponse>> {
  return apiFetch<DeleteCoachProjectResponse>(
    `/me/coach/projects/${encodeURIComponent(projectId)}`,
    { method: 'DELETE' },
  );
}
